import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { VariableSizeList } from 'react-window';

import { ListItem } from '../types';

const useVirtualScroll = (list: ListItem[], resizeDep?: number) => {
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  // The node the last measurement was taken from, held BOTH as a ref and as
  // state, because the two consumers need different things from it.
  //
  // The ref is what the parent-identity layout effect below compares against.
  // It has to be a ref: within a single commit the measuring effect runs before
  // that guard, and a state variable would still hold the PREVIOUS render's
  // node at that point, so the guard would re-measure a parent that had just
  // been measured — a second layout read on every mount.
  //
  // The state exists because a ref cannot be an effect dependency, and the
  // ResizeObserver effect has to re-subscribe when the node changes. Both are
  // written in `resize` below, which is the ONE place the parent is resolved.
  const measuredParent = useRef<HTMLElement | null>(null);
  const [observedParent, setObservedParent] = useState<HTMLElement | null>(
    null
  );

  const resize = useCallback(() => {
    const parent = ref.current?.parentElement ?? null;
    measuredParent.current = parent;
    setObservedParent(parent);
    const { height, width } = parent?.getBoundingClientRect() || {
      width: 0,
      height: 0,
    };
    setHeight(height);
    setWidth(width);
  }, [setHeight, setWidth, setObservedParent, ref]);

  // ISSUE-085. This measurement used to live in a passive `useEffect` whose dep
  // array carried a `DOMRect` read during the RENDER phase. `getBoundingClientRect`
  // returns a fresh object every call, so `Object.is` never matched and the effect
  // re-ran after every render of every list — 3 of every 4 layout reads on this
  // hook were that cascade, with no resize event involved.
  //
  // `useLayoutEffect` instead of `useEffect`: the read has to leave the render
  // phase, and the commit phase is the only other place it can happen before the
  // browser paints. Keeping the first non-zero measurement AHEAD of that paint is
  // a property this effect and the guard below hold jointly — not one this effect
  // holds alone, which is what an earlier draft of this comment claimed. Measured
  // against `useVirtualScroll.resizeThrottle.test.tsx`: demoting THIS effect
  // alone still paints the right size, because the guard runs in the same commit
  // and measures the parent it finds unmeasured; what it costs is a second layout
  // read per commit — 2 failed, the ISSUE-072 burst count and AC-1, with BOTH
  // paint-boundary tests green. Demoting both is what moves the measurement past
  // the paint, and then it is exactly the two paint-boundary tests that fail. So
  // neither effect may be passive, for two different reasons.
  //
  // The deps are `resize` (stable — see the `useCallback` above) and the caller's
  // explicit `resizeDep`. `list` is deliberately NOT a dep: a list change only
  // matters here if it changes the parent's box or produces the ref'd node, and
  // the two effects below report both of those directly instead of guessing at
  // them from a re-render.
  useLayoutEffect(() => {
    resize();
  }, [resize, resizeDep]);

  // The render-phase `DOMRect` dep this issue deleted was standing in for TWO
  // capabilities, not one:
  //
  //   (a) MEASURE ONCE THE REF'D ELEMENT EXISTS. `List` renders the node this
  //       ref is attached to only when `list.length > 0` (`List.tsx:77,97-105`),
  //       so every panel's list mounts with `ref.current === null` and nothing
  //       to measure. Before this issue, the node appearing on a later render
  //       flipped the render-phase rect from `undefined` to a real one and the
  //       measuring effect re-ran on that identity change. With that dep gone,
  //       and `resizeDep` being a splitter ratio rather than anything to do with
  //       `list` (`VoiceFilesPanel.tsx:42,52-53,63`, `MssPage.tsx:40,42`),
  //       nothing re-ran: the first imported file landed in a 0x0 react-window
  //       viewport until the window was resized. This effect restores (a).
  //
  //   (b) RE-MEASURE ON A BOX CHANGE THAT NO WINDOW RESIZE ACCOMPANIES — the
  //       ResizeObserver effect below.
  //
  // No dep array on purpose: this runs after every commit. It still performs NO
  // layout read unless the parent node itself changed identity —
  // `ref.current?.parentElement` is a property access, not a measurement, and
  // the guard returns before `resize()` whenever it matches what was last
  // measured. A re-render for an unrelated reason therefore still costs 0 reads.
  //
  // `useLayoutEffect` rather than `useEffect`, and here that is load-bearing on
  // its own: the appearance in (a) is the FIRST moment a real `List` has anything
  // to measure, so a passive effect would paint one frame of 0x0 file list on the
  // first import. Nothing else in the hook covers that moment — measured,
  // demoting ONLY this effect fails exactly one test, the appearance
  // paint-boundary test at the end of `useVirtualScroll.resizeThrottle.test.tsx`,
  // and leaves the other 15 green.
  //
  // A callback ref was the alternative and was rejected: `List.tsx:103,109`
  // hands this one ref OBJECT to two attachment points — `<section ref={ref}>`
  // and react-window's `outerRef` — and React's child-before-parent ref commit
  // makes the outer `<section>` the last writer, which is the element that must
  // be measured from. A callback ref would be invoked once per attachment with
  // two different nodes and the hook would have to arbitrate between them,
  // changing which element the measurement is relative to.
  useLayoutEffect(() => {
    if ((ref.current?.parentElement ?? null) === measuredParent.current) return;
    resize();
  });

  // Capability (b): re-measure when the parent's box changes for a reason that
  // is not a window resize — a sibling panel being dragged, a container growing
  // with its content. Observing the parent says that directly; re-reading its
  // rect on every render only approximated it, at the cost of a layout read per
  // render. The subscription target is `observedParent` rather than
  // `ref.current?.parentElement` so that this effect re-runs — and re-observes —
  // when the node the effect above found swaps or first appears.
  //
  // The `typeof` guard is for jsdom, which implements no ResizeObserver. Suites
  // that render a `List` without stubbing it themselves die on "ResizeObserver
  // is not defined" without the guard — measured on the tree this landed in by
  // deleting it: 2 suites, 14 tests, both under `src/pages/cvc/GeneratePanel/`.
  // Where the API is absent the hook still measures on mount, whenever the ref'd
  // node's parent appears or changes, and on every window resize; the only
  // capability it loses is (b).
  useEffect(() => {
    if (!observedParent || typeof ResizeObserver === 'undefined')
      return undefined;

    const observer = new ResizeObserver(resize);
    observer.observe(observedParent);
    return () => observer.disconnect();
  }, [resize, observedParent]);

  const listRef = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [list, listRef]);

  useEffect(() => {
    // Coalesce a burst of resize events into one layout read per animation
    // frame. `rafId` is declared inside the effect body, not in a ref, so the
    // cleanup's cancel belongs to the same closure that queued the frame.
    // The measuring layout effect above stays synchronous — only the listener is
    // deferred.
    let rafId = 0;
    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(resize);
    };

    window.addEventListener('resize', onResize);
    return () => {
      // `cancelAnimationFrame(0)` is a defined no-op, so no null guard.
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, [resize]);

  return { height, width, ref, listRef };
};
export default useVirtualScroll;
